/**
 * De merken van diensten waar we naar verwijzen.
 *
 * Als vectoren en niet als letters in een vierkantje. Een "D" in een doosje
 * zegt niets — mensen herkennen deze diensten aan hun vorm en kleur, en dat is
 * precies het punt van een knop die zegt "hier gaat je album heen". Ze zitten
 * ook niet in `icons.tsx`: dat zijn onze eigen tekens, die meekleuren met
 * `currentColor`. Deze hebben hun eigen kleuren en horen die te houden, ook in
 * donkere modus — een Google-G die opeens crème is, is geen Google-G meer.
 *
 * De vormen zijn nagetekend naar de officiële merken. Ze staan hier alleen om
 * naar die dienst te verwijzen.
 */

interface BrandProps {
  className?: string
}

export function GoogleMark({ className }: BrandProps) {
  return (
    <svg viewBox="0 0 18 18" className={className} role="img" aria-label="Google">
      <path
        fill="#4285F4"
        d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.344 0-4.3282-1.5831-5.036-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.9965 8.9965 0 0 0 0 9c0 1.4523.3477 2.8268.9573 4.0418L3.964 10.71z"
      />
      <path
        fill="#EA4335"
        d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.6559 3.5795 9 3.5795z"
      />
    </svg>
  )
}

/**
 * Het appeltje staat in `currentColor` en niet in het zwart. Op de knop is dat
 * de tekstkleur, dus in donkere modus wordt hij licht — en dat hoort ook: het
 * merk schrijft zelf voor dat hij zwart of wit is, welke van de twee hangt af
 * van waar hij op ligt.
 */
export function AppleMark({ className }: BrandProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} role="img" aria-label="Apple">
      <path
        fill="currentColor"
        d="M17.05 12.536c-.024-2.69 2.196-3.98 2.296-4.042-1.25-1.83-3.196-2.08-3.887-2.107-1.655-.168-3.23.975-4.07.975-.84 0-2.132-.951-3.505-.925-1.802.027-3.464 1.047-4.392 2.66-1.874 3.25-.479 8.061 1.343 10.7.892 1.29 1.955 2.74 3.35 2.688 1.343-.053 1.85-.869 3.474-.869 1.624 0 2.08.869 3.5.842 1.445-.024 2.36-1.317 3.242-2.612 1.021-1.498 1.44-2.948 1.464-3.022-.032-.013-2.807-1.077-2.835-4.278zM14.63 4.16c.741-.898 1.24-2.148 1.104-3.393-1.067.043-2.357.71-3.122 1.607-.688.797-1.288 2.068-1.128 3.29 1.189.093 2.405-.605 3.146-1.504z"
      />
    </svg>
  )
}

export function DriveMark({ className }: BrandProps) {
  return (
    <svg viewBox="0 0 87.3 78" className={className} role="img" aria-label="Google Drive">
      <path
        fill="#0066da"
        d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5z"
      />
      <path
        fill="#00ac47"
        d="M43.65 25 29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44A9.06 9.06 0 0 0 0 53h27.5z"
      />
      <path
        fill="#ea4335"
        d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l5.85 11.5z"
      />
      <path
        fill="#00832d"
        d="M43.65 25 57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.45-4.5 1.2z"
      />
      <path
        fill="#2684fc"
        d="M59.8 53H27.5L13.75 76.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
      />
      <path
        fill="#ffba00"
        d="M73.4 26.5 60.7 4.5c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
      />
    </svg>
  )
}

export function DropboxMark({ className }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} role="img" aria-label="Dropbox">
      <g fill="#0061FF">
        <path d="M8 2 0 7.2l8 5.2 8-5.2z" />
        <path d="M24 2l-8 5.2 8 5.2 8-5.2z" />
        <path d="M0 17.6l8-5.2 8 5.2-8 5.2z" />
        <path d="M32 17.6l-8-5.2-8 5.2 8 5.2z" />
        <path d="M8 24.6l8-5.2 8 5.2-8 5.2z" />
      </g>
    </svg>
  )
}

export function OneDriveMark({ className }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} role="img" aria-label="OneDrive">
      <path fill="#0364B8" d="M11.8 12.3 18 8.2l7.9 3.3.1-.6a6 6 0 0 0-11.4-2 5 5 0 0 0-2.8 3.4z" />
      <path
        fill="#0078D4"
        d="M11.8 12.3a5 5 0 0 0-2.9-.8 5.2 5.2 0 0 0-4.7 4l6.5 3.7 3.8-1.6 5-4-2.4-1z"
      />
      <path fill="#1490DF" d="M25.9 11.5h-.4c-.9 0-1.7.2-2.4.6l-3.6 2.1 3.3 4.1 5-1.7z" />
      <path
        fill="#28A8EA"
        d="M10.7 19.2 4.2 15.5A5.3 5.3 0 0 0 8.5 24h16.1a4.6 4.6 0 0 0 3.6-7.4l-5.4 1.7z"
      />
    </svg>
  )
}

/**
 * Immich heeft geen los beeldmerk in één kleur; het is een lens die van paars
 * naar oranje loopt. Het verloop krijgt een eigen id per gebruik zou netter
 * zijn, maar dit teken staat één keer op een scherm.
 */
export function ImmichMark({ className }: BrandProps) {
  return (
    <svg viewBox="0 0 32 32" className={className} role="img" aria-label="Immich">
      <defs>
        <linearGradient id="immich-lens" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#4250AF" />
          <stop offset="55%" stopColor="#AC51B5" />
          <stop offset="100%" stopColor="#FF6B6B" />
        </linearGradient>
      </defs>

      <circle cx="16" cy="16" r="14" fill="url(#immich-lens)" />
      <path
        fill="#fff"
        d="M16 6.6a9.4 9.4 0 0 1 8 4.5h-8a4.9 4.9 0 0 0-4.6 3.2L7.5 9.9A9.4 9.4 0 0 1 16 6.6zM6.6 16c0-1.3.3-2.6.8-3.8l4 6.9a4.9 4.9 0 0 0 4.6 2.4l-3.9 4.2A9.4 9.4 0 0 1 6.6 16zm18.9 3.6-4-6.9a4.9 4.9 0 0 0-1.6-1.7l5.6-1.5a9.4 9.4 0 0 1-.1 10.1z"
      />
      <circle cx="16" cy="16" r="3.1" fill="#fff" />
    </svg>
  )
}
